import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from "react";
import {
  StoredUser,
  UserProfile,
  clearStoredAuth,
  getStoredAuth,
  setStoredAuth,
} from "../utils/authStorage";
import { fetchMyProfile } from "../services/profileApi";

// Window event dispatched by apiClient on a 401 response — lets the auth
// state react to expired/invalid tokens without coupling apiClient to React.
export const AUTH_EXPIRED_EVENT = "capcam:auth:expired";

interface AuthState {
  token: string | null;
  user: StoredUser | null;
  profile: UserProfile | null;
}

interface AuthContextValue extends AuthState {
  isAuthenticated: boolean;
  login: (token: string, user: StoredUser) => void;
  logout: () => void;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const EMPTY_STATE: AuthState = { token: null, user: null, profile: null };

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<AuthState>(EMPTY_STATE);
  // Guard against calling fetchProfile before login state is committed.
  const tokenRef = useRef<string | null>(null);

  // Hydrate from localStorage on mount.
  useEffect(() => {
    const stored = getStoredAuth();
    if (stored) {
      tokenRef.current = stored.token;
      setState({
        token: stored.token,
        user: stored.user,
        profile: stored.profile ?? null,
      });
    }
  }, []);

  // After hydration, refresh the profile from the server so presigned
  // avatar URLs and counts stay up-to-date.
  const didFetchOnMount = useRef(false);
  useEffect(() => {
    if (didFetchOnMount.current) return;
    if (!tokenRef.current) return;
    didFetchOnMount.current = true;
    fetchMyProfile()
      .then((p) => {
        setState((prev) => {
          if (!prev.token || !prev.user) return prev;
          const next = { ...prev, profile: p };
          setStoredAuth({ token: prev.token, user: prev.user, profile: p });
          return next;
        });
      })
      .catch(() => {
        // Non-fatal — cached profile (if any) is still usable.
      });
  }, [state.token]); // trigger once token is hydrated

  const login = useCallback((token: string, user: StoredUser) => {
    tokenRef.current = token;
    setStoredAuth({ token, user });
    setState({ token, user, profile: null });
  }, []);

  const logout = useCallback(() => {
    tokenRef.current = null;
    clearStoredAuth();
    setState(EMPTY_STATE);
  }, []);

  const refreshProfile = useCallback(async () => {
    if (!tokenRef.current) return;
    try {
      const p = await fetchMyProfile();
      setState((prev) => {
        if (!prev.token || !prev.user) return prev;
        const next = { ...prev, profile: p };
        setStoredAuth({ token: prev.token, user: prev.user, profile: p });
        return next;
      });
    } catch {
      // Non-fatal.
    }
  }, []);

  // React to 401s from apiClient.
  useEffect(() => {
    const handler = () => logout();
    window.addEventListener(AUTH_EXPIRED_EVENT, handler);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, handler);
  }, [logout]);

  const value = useMemo<AuthContextValue>(
    () => ({
      token: state.token,
      user: state.user,
      profile: state.profile,
      isAuthenticated: !!state.token,
      login,
      logout,
      refreshProfile,
    }),
    [state.token, state.user, state.profile, login, logout, refreshProfile]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside an <AuthProvider>");
  }
  return ctx;
};
